import React, { useEffect, useState } from 'react';
import {array, bool, func, object, string} from 'prop-types';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import _ from 'lodash';

import {connect} from 'react-redux';

import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';
import AutoComplete from '../../components/FormFields/Autocomplete';
import SensorWarningModal from './SensorWarningModal';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as BasAgentActions from '../../actions/basAgentActions';
import * as DeviceActions from '../../actions/deviceActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .max(100)
        .required(),
    parent: Yup.string()
        .required(),
    agent: Yup.string()
        .required(),
    device: Yup.string()
        .required(),
    deviceObject: Yup.string()
        .required(),
    dataType: Yup.string()
        .required(),
    sensorType: Yup.object()
        .required(),
    unit: Yup.object()
        .nullable()
        .when('dataType', {
            is:   'float',
            then: Yup.object().required()
        }),
    displayUnit: Yup.object()
        .nullable()
        .when('displayDataType', {
            is:   'float',
            then: Yup.object().required()
        }),
    displayDataType: Yup.string(),
    conversion:      Yup.string()
        .nullable()
});

const CreateSensorModal = ({
    agents,
    clearErrors,
    close,
    conversions,
    companyInfo,
    deviceObjects,
    devices,
    errors,
    fetchConversions,
    isLoading,
    open,
    selectedNode,
    sensors,
    sensorTypes,
    title,
    toggleLoading,
    units,
    ...props
}) => {
    const [createAnother, setCreateAnother] = useState(false);
    const [filteredConversions, setFilteredConversions] = useState([]);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [loadingDeviceObjects, setLoadingDeviceObjects] = useState(false);
    const [displayUnitTypeDisabled, setDisplayUnitTypeDisabled] = useState(false);
    const [unitTypeDisabled, setUnitTypeDisabled] = useState(false);
    const [displayUnits, setDisplayUnits] = useState([]);
    const [activeDisplayUnit, setActiveDisplayUnit] = useState({});
    const [defaultAgentId, setDefaultAgentId] = useState('');
    const [defaultDeviceId, setDefaultDeviceId] = useState('');
    const [distinctDeviceList, setDistinctDeviceList] = useState('');
    const [filteredAgentList, setFilteredAgentList] = useState([]);

    const [activeAgent, setActiveAgent] = useState({});
    const [warningModalOpen, setWarningModalOpen] = useState(false);
    const [warningFunction, setWarningFunction] = useState(false);

    let distinctAgentList = [];

    const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

    const formik = useFormik({
        initialValues: {
            name:            '',
            parent:          _.get(selectedNode, 'name', ''),
            agent:           defaultAgentId,
            device:          defaultDeviceId,
            deviceObject:    '',
            dataType:        '',
            sensorType:      '',
            unit:            {},
            displayUnit:     {},
            displayDataType: '',
            conversion:      ''
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting, resetForm }) => {
            const {
                createSensor,
                companyInfo
            } = props;

            let isCreated;

            const parents = selectedNode.parent.split('/');
            const siteId = parents[parents.length - 2];

            const presentationUnit = values.displayUnit.id || activeDisplayUnit.id || values.unit.id;

            const body = {
                name:               values.name,
                sensorTypeId:       values.sensorType.id,
                rawUnitId:          values.unit.id,
                presentationUnitId: presentationUnit,
                equipmentId:        '',
                systemId:           '',
                deviceObjectId:     values.deviceObject,
                conversionId:       values.conversion === 'No Conversion' ? '' : values.conversion
            };

            if (selectedNode.type === 'equipmentItem') {
                body.equipmentId = selectedNode.id;
            } else if (selectedNode.type === 'systemItem') {
                body.systemId = selectedNode.id;
            }

            body.calculatedSensor = false;
            body.sensorCalculationId = null;
            body.calculatedSensorIds = null;

            clearErrors();
            setSubmitting(true);

            const handleSubmit = async () => {
                if (!isLoading) {
                    setWarningModalOpen(false);
                    toggleLoading(true);
                    isCreated = await createSensor(companyInfo, siteId, selectedNode.id, body);
                }

                if (isCreated) {
                    if (createAnother) {
                        formik.setFieldValue('name', '', false);
                        formik.setFieldValue('deviceObject', '', false);
                        formik.setFieldValue('dataType', '', false);
                        formik.setFieldValue('sensorType', '', false);
                        formik.setFieldValue('unit', '', false);
                        formik.setFieldValue('displayUnit', '', false);
                        formik.setFieldValue('displayDataType', '', false);
                        formik.setFieldValue('conversion', '', false);

                        formik.setTouched({
                            name:         false,
                            deviceObject: false,
                            dataType:     false,
                            sensorType:   false,
                            unit:         false
                        });

                        setSubmitting(false);
                        toggleLoading(false);

                        return;
                    }

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            };

            if (activeAgent.sensorCount >= 4000) {
                setWarningModalOpen(true);
                setWarningFunction(() => () => handleSubmit());
                return;
            }

            try {
                handleSubmit();
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    useEffect(() => {
        const init = async () => {
            if (sensors.length > 0) {
                const distinctAgents = [...new Set(sensors.map(sensor => sensor.agentId))];
                const distinctDevices = [...new Set(sensors.map(sensor => sensor.deviceId))];
                distinctAgentList = distinctAgents.filter(s => s !== EMPTY_GUID);
                setDistinctDeviceList(distinctDevices.filter(s => s !== EMPTY_GUID));
                await setDefaultAgentAndDevice();
            }
        };

        if (sensors.length > 0 && open) init();
    }, [sensors, open]);

    useEffect(() => {
        const init = async () => {
            await fetchConversions(companyInfo.companyId);
        };

        if (companyInfo.companyId) init();
    }, [companyInfo.companyId]);

    useEffect(() => {
        if (!_.isEmpty(conversions)) {
            setFilteredConversions(refineConversions(formik.values.deviceObject));
        }
    }, [formik.values.deviceObject]);

    useEffect(() => {
        const init = async () => {
            const {fetchDeviceObjects} = props;
            if (distinctDeviceList.length === 1 && distinctDeviceList[0] !== EMPTY_GUID) {
                const distinctDeviceId = distinctDeviceList[0];
                
                const foundDevice = devices.find(device => device.id === distinctDeviceId);
                if (foundDevice) {
                    formik.setFieldValue('device', distinctDeviceId, true);
                    setDefaultDeviceId(distinctDeviceId);
                    await fetchDeviceObjects(distinctDeviceId);
                }
            }
        };
        if (devices) init();
    }, [devices]);

    useEffect(() => {
        if (selectedNode && (selectedNode.type === 'equipmentItem' || selectedNode.type === 'systemItem')) {
            if (selectedNode.activeNode && selectedNode.activeNode.siteId) {
                const siteId = selectedNode.activeNode.siteId;
                const siteNotNull = _.cloneDeep(agents.filter(a => a.site !== null));
                const list = siteNotNull.filter(s => {
                    if (s.site?.find(site => site.id === siteId)) {
                        return true;
                    }
                    else {
                        return false;
                    }
                });
                setFilteredAgentList(list);
            }
            
        }
    }, [selectedNode]);

    const setDefaultAgentAndDevice = async () => {
        if (distinctAgentList.length === 1 && distinctAgentList[0] !== EMPTY_GUID) {
            const {fetchDevicesList} = props;
            // make sure this agent is in the list of agents
            const agentId = distinctAgentList[0];
            const foundAgent = agents.find(agent => agent.id === agentId);
            if (foundAgent) {
                formik.setFieldValue('agent', agentId, true);
                setActiveAgent(foundAgent);
                setDefaultAgentId(agentId);
                // load the device list
                const configurationId = foundAgent.driverConfiguration[0].id;
                setLoadingDevices(true);
                await fetchDevicesList(agentId, configurationId);
                setLoadingDevices(false);
            }
        }
    };

    const handleConversionChange = event => {
        const { value } = event.target;
        let targetConversion;

        const conversion = _.find(conversions, item => item.id === value);

        if (value === 'No Conversion' || !value.length) {
            targetConversion = getDataType(formik.values.deviceObject || '');
        } else {
            targetConversion = _.get(conversion, 'displayDataType', '');
        }

        if (value === 'No Conversion' || !value.length || _.get(conversion, 'displayDataType', '') === 'float') {

            const rawUnitId = formik.values.unit.id;
            const displayUnitId = formik.values.displayUnit.id || formik.values.unit.id;

            if (!_.isNil(rawUnitId) && rawUnitId.length) {
                formik.setFieldValue('rawUnit', getUnit(rawUnitId), false);
            }
            else {
                formik.setFieldValue('rawUnit', null);
            }

            if (!_.isNil(displayUnitId) && displayUnitId.length) {
                formik.setFieldValue('displayUnit', activeDisplayUnit || getUnit(displayUnitId), false);
            }
            else {
                formik.setFieldValue('displayUnit', null);
            }
        }

        if (targetConversion === 'float') {
            setDisplayUnitTypeDisabled(false);
        } else {
            setDisplayUnitTypeDisabled(true);
            formik.setFieldValue('rawUnit', {}, false);
            formik.setFieldValue('displayUnit', {}, false);
        }

        formik.setFieldValue('displayDataType', targetConversion, false);

        toggleLoading(false);

        formik.handleChange(event);
    };

    const getUnit = unitId => {
        let targetUnit;

        units.forEach(unit => {
            unit.items.forEach(item => {
                if (item.id === unitId) {
                    targetUnit = { ...item };
                }
            });
        });

        return targetUnit;
    };

    const getDataType = deviceObjectId => {
        const deviceObject = _.find(deviceObjects, item => item.id === deviceObjectId);
        return _.get(deviceObject, 'dataType', '').toLowerCase();
    };

    const refineConversions = deviceObjectId => {
        const dataType = getDataType(deviceObjectId);
        return conversions.filter(conversion => conversion.rawDataType === dataType);
    };

    const handleSensorTypeChange = event => {
        const {value} = event.target;

        if (formik.values.name) {
            formik.handleChange(event);

            return;
        }

        formik.setFieldValue('name', value.name, false);

        formik.handleChange(event);
    };

    const handleAgentChange = async event => {
        setLoadingDevices(true);
        const {fetchDevicesList, setAgent, setDeviceObjects} = props;
        const {value} = event.target;

        formik.setFieldValue('device', '', false);
        formik.setFieldValue('deviceObject', '', false);

        formik.setTouched({
            device:       false,
            deviceObject: false
        });

        toggleLoading(false);

        setAgent(null);
        const selectedAgent = agents.find(agent => agent.id === value);
        setActiveAgent(selectedAgent);

        const configurationId = _.get(selectedAgent, 'driverConfiguration[0].id', '');

        await fetchDevicesList(value, configurationId);
        setDeviceObjects([]);
        formik.handleChange(event);
        setLoadingDevices(false);
    };

    const handleDeviceChange = async event => {
        setLoadingDeviceObjects(true);
        const {fetchDeviceObjects} = props;
        const {value} = event.target;

        formik.setFieldValue('deviceObject', '', false);

        formik.setTouched({
            deviceObject: false
        });

        toggleLoading(false);

        await fetchDeviceObjects(value);

        formik.handleChange(event);
        setLoadingDeviceObjects(false);
    };

    const handleDeviceObjectChange = event => {
        const { value } = event.target;

        const deviceObject = _.find(deviceObjects, item => item.id === value);
        const dataType = _.get(deviceObject, 'dataType', '').toLowerCase();

        setFilteredConversions(refineConversions(value));

        const conversionIsEmpty = _.chain(filteredConversions).find(conversion => conversion.id === formik.values.conversion).isEmpty().value();

        if (_.isEmpty(formik.values.conversion) || conversionIsEmpty) {
            formik.setFieldValue('displayDataType', dataType, false);

            formik.setFieldValue('conversion', '', false);
        }

        formik.setFieldValue('dataType', dataType, false);

        formik.setFieldValue('unit', '', false);
        formik.setFieldValue('displayUnit', '', false);

        if (dataType !== 'float') {
            setUnitTypeDisabled(true);
            setDisplayUnitTypeDisabled(true);
        } else {
            setUnitTypeDisabled(false);
        }

        toggleLoading(false);

        formik.handleChange(event);
    };

    const handleUnitChange = event => {
        formik.handleChange(event);
        let displayUnits;

        toggleLoading(false);

        units.forEach(group => {
            if (group.name === event.target.value.unitType) {
                displayUnits = [group];
                return;
            }
        });

        setDisplayUnits(displayUnits);

        if (!formik.values.displayUnit) {
            formik.setFieldValue('displayUnit', event.target.value);
        } else if (formik.values.displayUnit.unitType !== event.target.value.unitType) {
            formik.setFieldValue('displayUnit', {});
        }
    };

    const handleDisplayUnitChange = event => {
        formik.handleChange(event);
        setActiveDisplayUnit(event.target.value);
    };

    const handleClose = () => {
        distinctAgentList = [];
        setDistinctDeviceList([]);
        setDefaultAgentId('');
        setDefaultDeviceId('');
        toggleLoading(false);
        clearErrors();

        close();
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Create"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/sensorTreeIcon.svg" alt="Sensor Icon" />
                        <span>New Sensor</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Select an Agent configured in IoT Hub.
                        </p>
                        <p>
                            Choose a Device from the selected Agent.
                        </p>
                        <p>
                            Choose a Device Object from the selected Device.
                        </p>
                        <p>
                            If no Agents, Devices or Device Objects are available, please configure those entities in IoT Hub.
                        </p>
                        <p>
                            Select a Sensor Type.
                        </p>
                        <p>
                            Enter a Sensor name.
                        </p>
                        <p>
                            If the Data Type is Float, you must specify a Unit. If the Data Type is Boolean, String or Integer, Units are not required.
                        </p>
                        <p>
                            Select a Conversion.
                        </p>
                        <p>
                            Note: Conversions are not required. Please see the help docs for more information on Conversions.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="agent"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.agent) && Boolean(formik.touched.agent)}
                            >
                                Agent
                            </InputLabel>
                            <Select
                                name="agent"
                                id="agent"
                                value={formik.values.agent}
                                onChange={handleAgentChange}
                                classes={{
                                    root: classes.select
                                }}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.agent) && Boolean(formik.touched.agent)}
                            >
                                {
                                    _.orderBy(filteredAgentList, [agent => agent.name.toLowerCase()], ['asc']).map(agent =>
                                        <MenuItem
                                            key={agent.id}
                                            value={agent.id}
                                        >
                                            {agent.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.agent &&
                             formik.errors.agent && <FormHelperText className={classes.error}>{formik.errors.agent}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="device"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.device) && Boolean(formik.touched.device)}
                            >
                                Device
                            </InputLabel>
                            <Select
                                name="device"
                                id="device"
                                disabled={!formik.values.agent || loadingDevices}
                                value={formik.values.device}
                                onChange={handleDeviceChange}
                                classes={{
                                    root: classes.select
                                }}
                                endAdornment={
                                    <InputAdornment position="end">
                                        {loadingDevices ?
                                            <CircularProgress className={classes.adornment} size={14} />
                                            :
                                            null
                                        }
                                    </InputAdornment>
                                }
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.device) && Boolean(formik.touched.device)}
                            >
                                {
                                    _.isEmpty(devices) ?
                                        <MenuItem
                                            key={'start'}
                                            disabled={true}
                                            value={'none'}
                                        >
                                            Agent has no devices
                                        </MenuItem>
                                        :
                                        null
                                }
                                {
                                    devices.map(deviceItem =>
                                        <MenuItem
                                            key={deviceItem.id}
                                            value={deviceItem.id}
                                        >
                                            {deviceItem.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.device &&
                             formik.errors.device && <FormHelperText className={classes.error}>{formik.errors.device}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="deviceObject"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.deviceObject) && Boolean(formik.touched.deviceObject)}
                            >
                                Device Object
                            </InputLabel>
                            <Select
                                name="deviceObject"
                                id="deviceObject"
                                disabled={!formik.values.device || loadingDeviceObjects}
                                value={formik.values.deviceObject}
                                onChange={handleDeviceObjectChange}
                                classes={{
                                    root: classes.select
                                }}
                                endAdornment={
                                    <InputAdornment position="end">
                                        {loadingDeviceObjects ?
                                            <CircularProgress className={classes.adornment} size={14} />
                                            :
                                            null
                                        }
                                    </InputAdornment>
                                }
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.deviceObject) && Boolean(formik.touched.deviceObject)}
                            >
                                {
                                    _.isEmpty(deviceObjects) ?
                                        <MenuItem
                                            key={'start'}
                                            disabled={true}
                                            value={'none'}
                                        >
                                            Device has no objects
                                        </MenuItem>
                                        :
                                        null
                                }
                                {
                                    deviceObjects.map(objectItem =>
                                        <MenuItem
                                            key={objectItem.id}
                                            value={objectItem.id}
                                        >
                                            {objectItem.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.deviceObject &&
                             formik.errors.deviceObject && <FormHelperText className={classes.error}>{formik.errors.deviceObject}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                disabled
                                id="parent"
                                name="parent"
                                type="text"
                                value={formik.values.parent}
                                label="Parent"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.parent) && Boolean(formik.touched.parent)}
                                helperText={Boolean(formik.touched.parent) && formik.errors.parent}
                            />
                        </FormControl>
                        <AutoComplete
                            grouped
                            label="Sensor Type"
                            name="sensorType"
                            onBlur={formik.handleBlur}
                            onChange={handleSensorTypeChange}
                            options={sensorTypes}
                            placeholder="Sensor Type"
                            value={formik.values.sensorType}
                            error={Boolean(formik.errors.sensorType) && Boolean(formik.touched.sensorType)}
                            helperText={Boolean(formik.touched.sensorType) && formik.errors.sensorType}
                        />
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Sensor Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                disabled
                                id="dataType"
                                name="dataType"
                                type="text"
                                value={formik.values.dataType}
                                label="Data Type"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.dataType) && Boolean(formik.touched.dataType)}
                                helperText={Boolean(formik.touched.dataType) && formik.errors.dataType}
                            />
                        </FormControl>
                        <AutoComplete
                            grouped
                            disabled={unitTypeDisabled}
                            label="Raw Unit"
                            name="unit"
                            onBlur={formik.handleBlur}
                            onChange={handleUnitChange}
                            options={units}
                            error={Boolean(formik.errors.unit) && Boolean(formik.touched.unit)}
                            helperText={Boolean(formik.errors.unit) && Boolean(formik.touched.unit) ? formik.errors.unit : null}
                            placeholder="Unit"
                            value={formik.values.unit}
                        />
                        <AutoComplete
                            grouped
                            disabled={displayUnitTypeDisabled}
                            label="Display Unit"
                            name="displayUnit"
                            onBlur={formik.handleBlur}
                            onChange={handleDisplayUnitChange}
                            options={displayUnits}
                            error={Boolean(formik.errors.displayUnit) && Boolean(formik.touched.displayUnit)}
                            helperText={Boolean(formik.errors.displayUnit) && Boolean(formik.touched.displayUnit) ? formik.errors.displayUnit : null}
                            placeholder="Display Unit"
                            value={formik.values.displayUnit}
                        />
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                disabled
                                id="displayDataType"
                                name="displayDataType"
                                type="text"
                                value={formik.values.displayDataType}
                                label="Display Data Type"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.displayDataType) && Boolean(formik.touched.displayDataType)}
                                helperText={Boolean(formik.touched.displayDataType) && formik.errors.displayDataType}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="conversion"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.conversion) && Boolean(formik.touched.conversion)}
                            >
                                Conversion
                            </InputLabel>
                            <Select
                                name="conversion"
                                id="conversion"
                                value={formik.values.conversion}
                                onChange={handleConversionChange}
                                classes={{
                                    root: classes.select
                                }}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.conversion) && Boolean(formik.touched.conversion)}
                            >
                                <MenuItem
                                    key={'No Conversion'}
                                    value={'No Conversion'}
                                >
                                    No Conversion
                                </MenuItem>
                                {
                                    filteredConversions.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.conversion &&
                             formik.errors.conversion && <FormHelperText className={classes.error}>{formik.errors.conversion}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <FormControlLabel
                                className={classes.checkboxLabel}
                                control={
                                    <Checkbox
                                        checked={createAnother}
                                        onChange={() => setCreateAnother(!createAnother)}
                                        name="checkbox"
                                        value="checkbox"
                                        color="primary"
                                    />
                                }
                                label="Create Another"
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
            {
                warningModalOpen ?
                    <SensorWarningModal
                        warningModalOpen={warningModalOpen}
                        setWarningModalOpen={setWarningModalOpen}
                        handleReset={formik.resetForm}
                        handleSubmit={warningFunction}
                    />
                    :
                    null
            }
        </EsmsDialog>
    );
};

CreateSensorModal.defaultProps = {
    agents:        [],
    close:         () => {},
    devices:       [],
    deviceObjects: [],
    open:          false,
    selectedNode:  {},
    sensorTypes:   [],
    title:         '',
    units:         []
};

CreateSensorModal.propTypes = {
    agents:        array,
    close:         func,
    devices:       array,
    deviceObjects: array,
    open:          bool,
    selectedNode:  object,
    sensorTypes:   array,
    title:         string,
    units:         array
};

const mapStateToProps = ({ basAgent, ...state }) => {
    const { basAgents } = basAgent;

    return {
        agents:        Object.values(basAgents),
        companyInfo:   state.companyInfo,
        conversions:   state.architect.conversions,
        devices:       state.device.devices,
        deviceObjects: state.device.deviceObjects,
        errors:        state.messages.errors,
        isLoading:     state.ui.isLoading,
        selectedNode:  state.architect.selectedNode,
        sensors:       state.architect.sensors,
        sensorTypes:   state.sensorTypes.data,
        units:         state.architect.rawUnits
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...BasAgentActions,
    ...DeviceActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateSensorModal);
